import { useState } from 'react'

import { circumference, ru } from '../../lib/physics'
import { Predict, Slider, useFrames } from './common'

/**
 * Откуда берётся π (урок 5): колесо делает один оборот по линейке, рядом откладываются его
 * диаметры. Путь всегда чуть больше трёх диаметров — для любого колеса. Отношение длины
 * окружности к диаметру — число π ≈ 3,14.
 */

const PX = 8 // пикселей на сантиметр
const X0 = 20
const ЗЕМЛЯ = 120

export default function PiLab() {
  const [d, setD] = useState(8) // см
  const [доля, setДоля] = useState(0) // часть оборота, 0…1
  const [идёт, setИдёт] = useState(false)

  const l = circumference(d)
  const r = (d / 2) * PX
  const путь = доля * l * PX
  const cx = X0 + r + путь
  const угол = доля * 360

  useFrames((dt) => {
    if (!идёт) return
    setДоля((s) => {
      const n = s + dt * 0.4
      if (n >= 1) {
        setИдёт(false)
        return 1
      }
      return n
    })
  })

  const готово = доля >= 1 && !идёт
  const целыхД = Math.floor(l / d)

  return (
    <div className="card">
      <svg className="lab-svg" viewBox="0 0 360 170" role="img" aria-label="Колесо катится один оборот">
        <line x1={10} y1={ЗЕМЛЯ} x2={350} y2={ЗЕМЛЯ} className="ink" strokeWidth={1.5} />
        {/* пройденный путь — развёрнутая окружность */}
        <line x1={X0 + r} y1={ЗЕМЛЯ} x2={X0 + r + путь} y2={ЗЕМЛЯ} className="ink-heavy" strokeWidth={4} />
        {/* диаметры, отложенные под дорогой */}
        {Array.from({ length: целыхД + 1 }, (_, i) => {
          const w = Math.min(d, l - i * d) * PX
          return w > 0.5 ? (
            <rect key={i} x={X0 + r + i * d * PX} y={ЗЕМЛЯ + 10} width={w} height={12} className={i % 2 ? 'fill-unit-b' : 'fill-unit-a'} />
          ) : null
        })}
        <text x={X0 + r} y={ЗЕМЛЯ + 38} className="lab-tick">диаметры колеса: три целых и ещё немного</text>
        {/* колесо */}
        <g transform={`rotate(${угол} ${cx} ${ЗЕМЛЯ - r})`}>
          <circle cx={cx} cy={ЗЕМЛЯ - r} r={r} className="fill-cabin ink-heavy" strokeWidth={3} />
          <line x1={cx - r} y1={ЗЕМЛЯ - r} x2={cx + r} y2={ЗЕМЛЯ - r} className="ink-el" strokeWidth={1.5} />
          <circle cx={cx} cy={ЗЕМЛЯ} r={3} className="fill-heavy" />
        </g>
      </svg>

      <div className="lab-readout">
        <span>диаметр d = {ru(d, 1)} см</span>
        <span className="heavy">путь за оборот l = {готово ? `${ru(l, 1)} см` : '…'}</span>
        <span>l : d = {готово ? <b>{ru(l / d, 2)}</b> : '…'}</span>
      </div>

      <Slider label="Диаметр колеса" value={d} min={3} max={10} step={0.5} unit="см" shown={ru(d, 1)} onChange={(v) => { setD(v); setДоля(0); setИдёт(false) }} />
      <div className="row-wrap" style={{ marginTop: 12 }}>
        <button className="btn btn-primary" onClick={() => { setДоля(0); setИдёт(true) }}>🛞 Прокатить один оборот</button>
      </div>

      <Predict
        question="Колесо диаметром 10 см сделало один оборот. Какой путь оно прошло?"
        options={['10 см', 'около 31 см', '100 см']}
        right={1}
        revealed={готово && Math.abs(d - 10) < 1e-9}
        explain="Путь за оборот равен длине окружности: l = π·d ≈ 3,14 · 10 см ≈ 31,4 см."
      />
      <p className="muted small">
        Меняй диаметр: отношение l : d каждый раз выходит 3,14. Это число назвали π. В уроке его
        нашли так же — штангенциркулем измерили диаметр стакана, а бумажной лентой — окружность.
      </p>
    </div>
  )
}
